import React, { useState, useEffect } from 'react';import {
  Briefcase,
  Package,
  ShoppingCart,
  FileText,
  Search,
  TrendingUp,
  Users,
  AlertCircle,
  CheckCircle,
  Clock,
  Send,
  Download
} from 'lucide-react';

const API_BASE = 'https://backend.monit.tech:444/watson';

const ApexDashboard = () => {
  const [activeModule, setActiveModule] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  const modules = [
    { id: 'dashboard', name: 'Dashboard', icon: TrendingUp },
    { id: 'recruitment', name: 'Recruitment', icon: Users },
    { id: 'orders', name: 'Order Management', icon: Package },
    { id: 'procurement', name: 'Procurement', icon: ShoppingCart }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <header className="bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-white p-2 rounded-lg">
                <Briefcase className="w-8 h-8 text-blue-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">APEX</h1>
                <p className="text-blue-100 text-sm">Enterprise which runs itself</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-blue-100">Powered by IBM Watsonx</p>
              <p className="text-xs text-blue-200">AI-Driven Operations</p>
            </div>
          </div>
        </div>
      </header>

      {notification && (
        <div
          className={`fixed top-20 right-6 z-50 px-6 py-4 rounded-lg shadow-xl ${
            notification.type === 'success' ? 'bg-green-500' : 'bg-red-500'
          } text-white flex items-center space-x-3`}
        >
          {notification.type === 'success' ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <AlertCircle className="w-5 h-5" />
          )}
          <span>{notification.message}</span>
        </div>
      )}

      <div className="flex">
        <aside className="w-64 bg-white shadow-lg min-h-screen">
          <nav className="p-4">
            {modules.map((module) => {
              const Icon = module.icon;
              return (
                <button
                  key={module.id}
                  onClick={() => setActiveModule(module.id)}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg mb-2 transition-all ${
                    activeModule === module.id
                      ? 'bg-blue-600 text-white shadow-md'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{module.name}</span>
                </button>
              );
            })}
          </nav>
        </aside>

        <main className="flex-1 p-8">
          {activeModule === 'dashboard' && <Dashboard />}
          {activeModule === 'recruitment' && (
            <RecruitmentModule
              showNotification={showNotification}
              loading={loading}
              setLoading={setLoading}
            />
          )}
          {activeModule === 'orders' && (
            <OrdersModule
              showNotification={showNotification}
              loading={loading}
              setLoading={setLoading}
            />
          )}
          {activeModule === 'procurement' && (
            <ProcurementModule
              showNotification={showNotification}
              loading={loading}
              setLoading={setLoading}
            />
          )}
        </main>
      </div>
    </div>
  );
};

const StatCard = ({ title, value, icon: Icon, color }) => {
  const colorClasses = {
    blue: 'from-blue-500 to-blue-600',
    green: 'from-green-500 to-green-600',
    orange: 'from-orange-500 to-orange-600'
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-600 text-sm mb-1">{title}</p>
          <p className="text-3xl font-bold text-gray-800">{value}</p>
        </div>
        <div className={`bg-gradient-to-br ${colorClasses[color]} p-4 rounded-lg`}>
          <Icon className="w-8 h-8 text-white" />
        </div>
      </div>
    </div>
  );
};

const QuickActionCard = ({ title, icon: Icon }) => {
  return (
    <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-4 rounded-lg hover:shadow-md transition-shadow cursor-pointer border border-gray-200">
      <div className="flex flex-col items-center text-center space-y-2">
        <div className="bg-blue-100 p-3 rounded-lg">
          <Icon className="w-6 h-6 text-blue-600" />
        </div>
        <p className="text-sm font-medium text-gray-700">{title}</p>
      </div>
    </div>
  );
};

const Dashboard = () => {
  return (
    <div>
      <h2 className="text-3xl font-bold text-gray-800 mb-6">Dashboard Overview</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard title="Active Recruitments" value="12" icon={Users} color="blue" />
        <StatCard title="Orders Today" value="48" icon={Package} color="green" />
        <StatCard title="Pending RFQs" value="7" icon={FileText} color="orange" />
      </div>

      <div className="mt-8 bg-white rounded-lg shadow-md p-6">
        <h3 className="text-xl font-bold text-gray-800 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <QuickActionCard title="Generate Job Post" icon={Users} />
          <QuickActionCard title="Track Order" icon={Search} />
          <QuickActionCard title="Create RFQ" icon={FileText} />
          <QuickActionCard title="Check Inventory" icon={Package} />
        </div>
      </div>
    </div>
  );
};

const RecruitmentModule = ({ showNotification, loading, setLoading }) => {
  const [jobTitle, setJobTitle] = useState('');
  const [jobPost, setJobPost] = useState('');
  const [cvData, setCvData] = useState(null);
  const [analysis, setAnalysis] = useState('');
  const [step, setStep] = useState(1);

  const generateJobPost = async () => {
    if (!jobTitle.trim()) {
      showNotification('Please enter a job title', 'error');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `${API_BASE}/generate-job-post?title=${encodeURIComponent(jobTitle)}`
      );
      const data = await response.json();
      setJobPost(data.job_description || data.description || JSON.stringify(data));
      setStep(2);
      showNotification('Job post generated successfully!');
    } catch (error) {
      showNotification('Error generating job post', 'error');
      console.error(error);
    }
    setLoading(false);
  };

  const getCVs = async () => {
    if (!jobPost.trim()) {
      showNotification('Please generate or enter a job post first', 'error');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `${API_BASE}/getCVs?job_post=${encodeURIComponent(jobPost)}`
      );
      const data = await response.json();
      setCvData(data);
      setStep(3);
      showNotification('CV retrieved successfully!');
    } catch (error) {
      showNotification('Error retrieving CV', 'error');
      console.error(error);
    }
    setLoading(false);
  };

  const analyzeApplication = async () => {
    if (!cvData) {
      showNotification('No CV data available to analyze', 'error');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: cvData.username,
          job_post: jobPost,
          cv_content: cvData.cv_text
        })
      });
      const data = await response.json();
      setAnalysis(data.analysis);
      setStep(4);
      showNotification('Analysis complete!');
    } catch (error) {
      showNotification('Error analyzing application', 'error');
      console.error(error);
    }
    setLoading(false);
  };

  return (
    <div>
      <h2 className="text-3xl font-bold text-gray-800 mb-6">Recruitment Management</h2>

      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex items-center justify-between mb-8">
          {[1, 2, 3, 4].map((num) => (
            <div key={num} className="flex items-center flex-1">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                  step >= num ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'
                }`}
              >
                {num}
              </div>
              {num < 4 && (
                <div
                  className={`flex-1 h-1 mx-2 ${
                    step > num ? 'bg-blue-600' : 'bg-gray-200'
                  }`}
                />
              )}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-4 gap-4 text-center text-sm">
          <div>Create Job Post</div>
          <div>Review Post</div>
          <div>Get CVs</div>
          <div>Analyze</div>
        </div>
      </div>

      {step === 1 && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-bold text-gray-800 mb-4">
            Generate Job Description
          </h3>
          <input
            type="text"
            value={jobTitle}
            onChange={(e) => setJobTitle(e.target.value)}
            placeholder="Enter job title (e.g., Senior Software Engineer)"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-4"
          />
          <button
            onClick={generateJobPost}
            disabled={loading}
            className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 font-medium flex items-center justify-center space-x-2"
          >
            {loading ? <Clock className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
            <span>{loading ? 'Generating...' : 'Generate Job Post'}</span>
          </button>
        </div>
      )}

      {step === 2 && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-bold text-gray-800 mb-4">Review Job Post</h3>
          <textarea
            value={jobPost}
            onChange={(e) => setJobPost(e.target.value)}
            className="w-full h-64 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-4"
          />
          <button
            onClick={getCVs}
            disabled={loading}
            className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 font-medium"
          >
            {loading ? 'Loading CVs...' : 'Find Matching CVs'}
          </button>
        </div>
      )}

      {step === 3 && cvData && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-bold text-gray-800 mb-4">
            Candidate: {cvData.username}
          </h3>
          <div className="bg-gray-50 p-4 rounded-lg mb-4 max-h-96 overflow-y-auto">
            <pre className="whitespace-pre-wrap text-sm">{cvData.cv_text}</pre>
          </div>
          <button
            onClick={analyzeApplication}
            disabled={loading}
            className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 font-medium"
          >
            {loading ? 'Analyzing...' : 'Analyze Candidate Fit'}
          </button>
        </div>
      )}

      {step === 4 && analysis && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-bold text-gray-800 mb-4">AI Analysis Results</h3>
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg">
            <pre className="whitespace-pre-wrap text-sm">{analysis}</pre>
          </div>
          <button
            onClick={() => {
              setStep(1);
              setJobTitle('');
              setJobPost('');
              setCvData(null);
              setAnalysis('');
            }}
            className="w-full mt-4 bg-gray-600 text-white py-3 rounded-lg hover:bg-gray-700 transition-colors font-medium"
          >
            Start New Recruitment
          </button>
        </div>
      )}
    </div>
  );
};

const OrdersModule = ({ showNotification, loading, setLoading }) => {
  const [orderId, setOrderId] = useState('');
  const [orderStatus, setOrderStatus] = useState(null);
  const [productName, setProductName] = useState('');
  const [latestOrders, setLatestOrders] = useState([]);

  const trackOrder = async () => {
    if (!orderId.trim()) {
      showNotification('Please enter an order ID', 'error');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `${API_BASE}/track-order?order_id=${encodeURIComponent(orderId)}`
      );
      const data = await response.json();
      setOrderStatus(data);
      showNotification('Order status retrieved!');
    } catch (error) {
      showNotification('Error tracking order', 'error');
      console.error(error);
    }
    setLoading(false);
  };

  const fetchLatestOrders = async () => {
    if (!productName.trim()) {
      showNotification('Please enter a product name', 'error');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `${API_BASE}/latest-orders?product_name=${encodeURIComponent(
          productName
        )}&max_orders=3`
      );
      const data = await response.json();
      setLatestOrders(data);
      showNotification('Latest orders retrieved!');
    } catch (error) {
      showNotification('Error fetching orders', 'error');
      console.error(error);
    }
    setLoading(false);
  };

  return (
    <div>
      <h2 className="text-3xl font-bold text-gray-800 mb-6">Order Management</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-bold text-gray-800 mb-4">Track Order</h3>
          <input
            type="text"
            value={orderId}
            onChange={(e) => setOrderId(e.target.value)}
            placeholder="Enter Order ID (e.g., ORD-2024-8734)"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-4"
          />
          <button
            onClick={trackOrder}
            disabled={loading}
            className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 font-medium"
          >
            {loading ? 'Tracking...' : 'Track Order'}
          </button>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-bold text-gray-800 mb-4">Latest Orders</h3>
          <input
            type="text"
            value={productName}
            onChange={(e) => setProductName(e.target.value)}
            placeholder="Enter Product Name (e.g., APEX SmartWatch X7)"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-4"
          />
          <button
            onClick={fetchLatestOrders}
            disabled={loading}
            className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 font-medium"
          >
            {loading ? 'Fetching...' : 'Get Latest Orders'}
          </button>
        </div>
      </div>

      {orderStatus && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h3 className="text-xl font-bold text-gray-800 mb-4">
            Order Status: {orderStatus.order_id}
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">Status</p>
              <p className="text-lg font-semibold text-blue-600">
                {orderStatus.status}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Product</p>
              <p className="text-lg font-semibold">
                {orderStatus.product_name || 'N/A'}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Quantity</p>
              <p className="text-lg font-semibold">
                {orderStatus.quantity || 'N/A'}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">City</p>
              <p className="text-lg font-semibold">{orderStatus.city || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Last Updated</p>
              <p className="text-lg font-semibold">
                {orderStatus.last_updated || 'N/A'}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Est. Delivery</p>
              <p className="text-lg font-semibold">
                {orderStatus.estimated_delivery || 'N/A'}
              </p>
            </div>
          </div>
          <div className="mt-4 bg-blue-50 p-4 rounded-lg">
            <p className="text-sm text-gray-700">{orderStatus.message}</p>
          </div>
        </div>
      )}

      {latestOrders.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-bold text-gray-800 mb-4">Recent Orders</h3>
          <div className="space-y-4">
            {latestOrders.map((order, idx) => (
              <div
                key={idx}
                className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <p className="font-bold text-lg">{order.order_id}</p>
                    <p className="text-gray-600">{order.customer_name}</p>
                  </div>
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-medium ${
                      order.status === 'Delivered'
                        ? 'bg-green-100 text-green-800'
                        : order.status === 'In Transit'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}
                  >
                    {order.status}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Product</p>
                    <p className="font-medium">{order.product_name}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Quantity</p>
                    <p className="font-medium">{order.quantity}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Price</p>
                    <p className="font-medium">{order.price}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">City</p>
                    <p className="font-medium">{order.city}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Date</p>
                    <p className="font-medium">{order.order_date}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const ProcurementModule = ({ showNotification, loading, setLoading }) => {
  const [activeTab, setActiveTab] = useState('rfq');
  const [rfqData, setRfqData] = useState({
    item_name: '',
    quantity: '',
    delivery_location: '',
    delivery_deadline: '',
    specifications: '',
    additional_notes: '',
    supplier_name: ''
  });
  const [rfqResponse, setRfqResponse] = useState(null);

  const [inventoryProduct, setInventoryProduct] = useState('');
  const [inventoryData, setInventoryData] = useState(null);

  const [quoteData, setQuoteData] = useState({
    rfq_summary: '',
    supplier_name: '',
    quote_text: '',
    price: '',
    currency: 'PKR',
    lead_time_days: '',
    payment_terms: '',
    previous_supplier_price: ''
  });
  const [quoteResponse, setQuoteResponse] = useState(null);

  const [poData, setPoData] = useState({
    po_number: '',
    supplier_name: '',
    supplier_address: '',
    buyer_name: '',
    buyer_company: '',
    delivery_location: '',
    delivery_deadline: '',
    payment_terms: '',
    additional_terms: '',
    items: [
      { item_name: '', specifications: '', quantity: '', unit_price: '', currency: 'PKR' }
    ]
  });
  const [poResponse, setPoResponse] = useState(null);

  const generateRFQ = async () => {
    if (
      !rfqData.item_name ||
      !rfqData.quantity ||
      !rfqData.delivery_location ||
      !rfqData.delivery_deadline
    ) {
      showNotification('Please fill in all required fields', 'error');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/procurement/generate-rfq`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...rfqData,
          quantity: parseInt(rfqData.quantity, 10)
        })
      });
      const data = await response.json();
      setRfqResponse(data);
      showNotification('RFQ generated successfully!');
    } catch (error) {
      showNotification('Error generating RFQ', 'error');
      console.error(error);
    }
    setLoading(false);
  };

  const checkInventory = async () => {
    if (!inventoryProduct.trim()) {
      showNotification('Please enter a product name', 'error');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/procurement/check-inventory`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ product_name: inventoryProduct })
      });
      const data = await response.json();
      setInventoryData(data);
      showNotification('Inventory checked successfully!');
    } catch (error) {
      showNotification('Error checking inventory', 'error');
      console.error(error);
    }
    setLoading(false);
  };

  const analyzeQuote = async () => {
    if (!quoteData.rfq_summary || !quoteData.supplier_name || !quoteData.quote_text || !quoteData.price) {
      showNotification('Please fill in RFQ summary, supplier, quote text and price', 'error');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...quoteData,
        price: parseFloat(quoteData.price),
        lead_time_days: quoteData.lead_time_days
          ? parseInt(quoteData.lead_time_days, 10)
          : null,
        previous_supplier_price: quoteData.previous_supplier_price
          ? parseFloat(quoteData.previous_supplier_price)
          : null
      };

      const response = await fetch(`${API_BASE}/procurement/analyze-quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      setQuoteResponse(data);
      showNotification('Quote analyzed successfully!');
    } catch (error) {
      showNotification('Error analyzing quote', 'error');
      console.error(error);
    }
    setLoading(false);
  };

  const generatePO = async () => {
    if (
      !poData.po_number ||
      !poData.supplier_name ||
      !poData.supplier_address ||
      !poData.buyer_name ||
      !poData.buyer_company ||
      !poData.delivery_location ||
      !poData.delivery_deadline
    ) {
      showNotification('Please fill in all required Purchase Order fields', 'error');
      return;
    }

    const validItems = poData.items.filter(
      (item) => item.item_name && item.quantity && item.unit_price
    );

    if (validItems.length === 0) {
      showNotification('Please add at least one valid item', 'error');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...poData,
        items: validItems.map((item) => ({
          ...item,
          quantity: parseInt(item.quantity, 10),
          unit_price: parseFloat(item.unit_price)
        }))
      };

      const response = await fetch(`${API_BASE}/procurement/generate-po`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      setPoResponse(data);
      showNotification('Purchase Order generated successfully!');
    } catch (error) {
      showNotification('Error generating Purchase Order', 'error');
      console.error(error);
    }
    setLoading(false);
  };

  const updatePoItem = (index, field, value) => {
    const newItems = [...poData.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setPoData({ ...poData, items: newItems });
  };

  const addPoItem = () => {
    setPoData({
      ...poData,
      items: [
        ...poData.items,
        { item_name: '', specifications: '', quantity: '', unit_price: '', currency: 'PKR' }
      ]
    });
  };

  const removePoItem = (index) => {
    const newItems = poData.items.filter((_, idx) => idx !== index);
    setPoData({ ...poData, items: newItems });
  };

  return (
    <div>
      <h2 className="text-3xl font-bold text-gray-800 mb-6">Procurement Management</h2>

      <div className="bg-white rounded-lg shadow-md mb-6">
        <div className="flex border-b overflow-x-auto">
          <button
            onClick={() => setActiveTab('rfq')}
            className={`px-6 py-3 font-medium whitespace-nowrap ${
              activeTab === 'rfq' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600'
            }`}
          >
            Generate RFQ
          </button>
          <button
            onClick={() => setActiveTab('inventory')}
            className={`px-6 py-3 font-medium whitespace-nowrap ${
              activeTab === 'inventory'
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-600'
            }`}
          >
            Check Inventory
          </button>
          <button
            onClick={() => setActiveTab('quote')}
            className={`px-6 py-3 font-medium whitespace-nowrap ${
              activeTab === 'quote'
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-600'
            }`}
          >
            Analyze Quote
          </button>
          <button
            onClick={() => setActiveTab('po')}
            className={`px-6 py-3 font-medium whitespace-nowrap ${
              activeTab === 'po' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600'
            }`}
          >
            Generate PO
          </button>
        </div>
      </div>

      {activeTab === 'rfq' && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-bold text-gray-800 mb-4">Request for Quotation</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <input
              type="text"
              placeholder="Item Name *"
              value={rfqData.item_name}
              onChange={(e) => setRfqData({ ...rfqData, item_name: e.target.value })}
              className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <input
              type="number"
              placeholder="Quantity *"
              value={rfqData.quantity}
              onChange={(e) => setRfqData({ ...rfqData, quantity: e.target.value })}
              className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <input
              type="text"
              placeholder="Delivery Location *"
              value={rfqData.delivery_location}
              onChange={(e) =>
                setRfqData({ ...rfqData, delivery_location: e.target.value })
              }
              className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <input
              type="date"
              placeholder="Delivery Deadline *"
              value={rfqData.delivery_deadline}
              onChange={(e) =>
                setRfqData({ ...rfqData, delivery_deadline: e.target.value })
              }
              className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <input
              type="text"
              placeholder="Supplier Name (optional)"
              value={rfqData.supplier_name}
              onChange={(e) =>
                setRfqData({ ...rfqData, supplier_name: e.target.value })
              }
              className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <textarea
            placeholder="Specifications (optional)"
            value={rfqData.specifications}
            onChange={(e) =>
              setRfqData({ ...rfqData, specifications: e.target.value })
            }
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-4"
            rows={3}
          />
          <textarea
            placeholder="Additional Notes (optional)"
            value={rfqData.additional_notes}
            onChange={(e) =>
              setRfqData({ ...rfqData, additional_notes: e.target.value })
            }
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-4"
            rows={2}
          />
          <button
            onClick={generateRFQ}
            disabled={loading}
            className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 font-medium"
          >
            {loading ? 'Generating...' : 'Generate RFQ Email'}
          </button>

          {rfqResponse && (
            <div className="mt-6 bg-gradient-to-r from-green-50 to-blue-50 p-6 rounded-lg">
              <h4 className="font-bold text-lg mb-2">Subject:</h4>
              <p className="mb-4">{rfqResponse.rfq_email_subject}</p>
              <h4 className="font-bold text-lg mb-2">Email Body:</h4>
              <pre className="whitespace-pre-wrap text-sm bg-white p-4 rounded">
                {rfqResponse.rfq_email_body}
              </pre>
              <button className="mt-4 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2">
                <Download className="w-4 h-4" />
                <span>Download RFQ</span>
              </button>
            </div>
          )}
        </div>
      )}

      {activeTab === 'inventory' && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-bold text-gray-800 mb-4">Inventory Health Check</h3>
          <input
            type="text"
            value={inventoryProduct}
            onChange={(e) => setInventoryProduct(e.target.value)}
            placeholder="Enter Product Name (e.g., Laptop Pro 15)"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-4"
          />
          <button
            onClick={checkInventory}
            disabled={loading}
            className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 font-medium"
          >
            {loading ? 'Checking...' : 'Check Inventory'}
          </button>

          {inventoryData && (
            <div className="mt-6">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">Product</p>
                  <p className="text-lg font-bold">{inventoryData.product_name}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">Net Available</p>
                  <p className="text-lg font-bold">{inventoryData.net_available}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">Days of Cover</p>
                  <p className="text-lg font-bold">{inventoryData.days_of_cover}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">Status</p>
                  <p
                    className={`text-lg font-bold ${
                      inventoryData.status === 'Healthy'
                        ? 'text-green-600'
                        : inventoryData.status === 'Low'
                        ? 'text-yellow-600'
                        : 'text-red-600'
                    }`}
                  >
                    {inventoryData.status}
                  </p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">Recommended Reorder Qty</p>
                  <p className="text-lg font-bold">
                    {inventoryData.recommended_reorder_quantity}
                  </p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">Suggested Reorder Date</p>
                  <p className="text-lg font-bold">
                    {inventoryData.suggested_reorder_date || 'N/A'}
                  </p>
                </div>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm font-medium text-gray-700">Notes:</p>
                <p className="text-gray-800">{inventoryData.notes}</p>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'quote' && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-bold text-gray-800 mb-4">Analyze Supplier Quote</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <textarea
              placeholder="RFQ Summary *"
              value={quoteData.rfq_summary}
              onChange={(e) =>
                setQuoteData({ ...quoteData, rfq_summary: e.target.value })
              }
              className="md:col-span-2 w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={3}
            />
            <input
              type="text"
              placeholder="Supplier Name *"
              value={quoteData.supplier_name}
              onChange={(e) =>
                setQuoteData({ ...quoteData, supplier_name: e.target.value })
              }
              className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <input
              type="number"
              placeholder="Quoted Price *"
              value={quoteData.price}
              onChange={(e) => setQuoteData({ ...quoteData, price: e.target.value })}
              className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <input
              type="text"
              placeholder="Currency (default PKR)"
              value={quoteData.currency}
              onChange={(e) =>
                setQuoteData({ ...quoteData, currency: e.target.value })
              }
              className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <input
              type="number"
              placeholder="Lead Time (days, optional)"
              value={quoteData.lead_time_days}
              onChange={(e) =>
                setQuoteData({ ...quoteData, lead_time_days: e.target.value })
              }
              className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <input
              type="number"
              placeholder="Previous Supplier Price (optional)"
              value={quoteData.previous_supplier_price}
              onChange={(e) =>
                setQuoteData({
                  ...quoteData,
                  previous_supplier_price: e.target.value
                })
              }
              className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <textarea
            placeholder="Quote Text *"
            value={quoteData.quote_text}
            onChange={(e) => setQuoteData({ ...quoteData, quote_text: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-4"
            rows={4}
          />
          <textarea
            placeholder="Payment Terms (optional)"
            value={quoteData.payment_terms}
            onChange={(e) =>
              setQuoteData({ ...quoteData, payment_terms: e.target.value })
            }
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-4"
            rows={2}
          />
          <button
            onClick={analyzeQuote}
            disabled={loading}
            className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 font-medium"
          >
            {loading ? 'Analyzing...' : 'Analyze Quote'}
          </button>

          {quoteResponse && (
            <div className="mt-6 bg-gradient-to-r from-purple-50 to-blue-50 p-6 rounded-lg">
              <h4 className="font-bold text-lg mb-2">
                Supplier: {quoteResponse.supplier_name}
              </h4>
              <p className="mb-3 text-sm text-gray-700">{quoteResponse.summary}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 text-sm">
                <div className="bg-white p-4 rounded border">
                  <p className="font-semibold mb-1">Price Evaluation</p>
                  <p>{quoteResponse.price_evaluation}</p>
                </div>
                <div className="bg-white p-4 rounded border">
                  <p className="font-semibold mb-1">Risk Evaluation</p>
                  <p>{quoteResponse.risk_evaluation}</p>
                </div>
              </div>
              <div className="bg-white p-4 rounded border mb-4">
                <p className="font-semibold mb-1">Recommendation</p>
                <p className="text-sm">{quoteResponse.recommendation}</p>
              </div>
              {quoteResponse.negotiation_points && (
                <div className="bg-white p-4 rounded border">
                  <p className="font-semibold mb-2">Negotiation Points</p>
                  <ul className="list-disc list-inside text-sm space-y-1">
                    {quoteResponse.negotiation_points.map((point, idx) => (
                      <li key={idx}>{point}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {activeTab === 'po' && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-bold text-gray-800 mb-4">Generate Purchase Order</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <input
              type="text"
              placeholder="PO Number *"
              value={poData.po_number}
              onChange={(e) => setPoData({ ...poData, po_number: e.target.value })}
              className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <input
              type="text"
              placeholder="Supplier Name *"
              value={poData.supplier_name}
              onChange={(e) => setPoData({ ...poData, supplier_name: e.target.value })}
              className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <input
              type="text"
              placeholder="Supplier Address *"
              value={poData.supplier_address}
              onChange={(e) =>
                setPoData({ ...poData, supplier_address: e.target.value })
              }
              className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <input
              type="text"
              placeholder="Buyer Name *"
              value={poData.buyer_name}
              onChange={(e) => setPoData({ ...poData, buyer_name: e.target.value })}
              className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <input
              type="text"
              placeholder="Buyer Company *"
              value={poData.buyer_company}
              onChange={(e) =>
                setPoData({ ...poData, buyer_company: e.target.value })
              }
              className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <input
              type="text"
              placeholder="Delivery Location *"
              value={poData.delivery_location}
              onChange={(e) =>
                setPoData({ ...poData, delivery_location: e.target.value })
              }
              className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <input
              type="date"
              placeholder="Delivery Deadline *"
              value={poData.delivery_deadline}
              onChange={(e) =>
                setPoData({ ...poData, delivery_deadline: e.target.value })
              }
              className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <textarea
            placeholder="Payment Terms (optional)"
            value={poData.payment_terms}
            onChange={(e) =>
              setPoData({ ...poData, payment_terms: e.target.value })
            }
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-4"
            rows={2}
          />
          <textarea
            placeholder="Additional Terms (optional)"
            value={poData.additional_terms}
            onChange={(e) =>
              setPoData({ ...poData, additional_terms: e.target.value })
            }
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-6"
            rows={2}
          />

          <h4 className="text-lg font-semibold mb-3">Items</h4>
          <div className="space-y-4 mb-4">
            {poData.items.map((item, index) => (
              <div
                key={index}
                className="border border-gray-200 rounded-lg p-4 grid grid-cols-1 md:grid-cols-5 gap-3"
              >
                <input
                  type="text"
                  placeholder="Item Name *"
                  value={item.item_name}
                  onChange={(e) => updatePoItem(index, 'item_name', e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <input
                  type="text"
                  placeholder="Specifications"
                  value={item.specifications}
                  onChange={(e) =>
                    updatePoItem(index, 'specifications', e.target.value)
                  }
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <input
                  type="number"
                  placeholder="Quantity *"
                  value={item.quantity}
                  onChange={(e) => updatePoItem(index, 'quantity', e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <input
                  type="number"
                  placeholder="Unit Price *"
                  value={item.unit_price}
                  onChange={(e) => updatePoItem(index, 'unit_price', e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    placeholder="Currency"
                    value={item.currency}
                    onChange={(e) => updatePoItem(index, 'currency', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {poData.items.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removePoItem(index)}
                      className="text-red-500 text-sm"
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={addPoItem}
            className="mb-4 text-blue-600 text-sm font-medium"
          >
            + Add another item
          </button>

          <button
            onClick={generatePO}
            disabled={loading}
            className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 font-medium"
          >
            {loading ? 'Generating...' : 'Generate Purchase Order'}
          </button>

          {poResponse && (
            <div className="mt-6 bg-gradient-to-r from-amber-50 to-blue-50 p-6 rounded-lg">
              <h4 className="font-bold text-lg mb-2">PO Summary</h4>
              <p className="mb-4 text-sm text-gray-800">{poResponse.po_summary}</p>
              <h4 className="font-bold text-lg mb-2">PO Document</h4>
              <pre className="whitespace-pre-wrap text-sm bg-white p-4 rounded border">
                {poResponse.po_document_text}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ApexDashboard;
